import React from "react";
import { Paper, Title } from "@mantine/core";
import {
  CartesianGrid,
  Label,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { NPCTSnapshot, NPCTUserInput } from "types/testTypes";
import { computeDValueTicks, formatUserInputD } from "./NPCTAxisHelpers";

const DOWNSAMPLE = 4;

interface NPCTGazeAngleChartProps {
  snapshots: NPCTSnapshot[];
  userInputs: NPCTUserInput[];
}

export default function NPCTGazeAngleChart({ snapshots, userInputs }: NPCTGazeAngleChartProps) {
  const { t } = useTranslation();

  const leftEyeScatterData = React.useMemo(
    () =>
      snapshots
        .filter(
          (s, i) => i % DOWNSAMPLE === 0 && s.leftGazeValid && s.leftHorizGazeAngleDeg != null,
        )
        .map((s) => ({ x: s.leftHorizGazeAngleDeg!, y: s.timestamp })),
    [snapshots],
  );

  const rightEyeScatterData = React.useMemo(
    () =>
      snapshots
        .filter(
          (s, i) => i % DOWNSAMPLE === 0 && s.rightGazeValid && s.rightHorizGazeAngleDeg != null,
        )
        .map((s) => ({ x: 80 - s.rightHorizGazeAngleDeg!, y: s.timestamp })),
    [snapshots],
  );

  const gazeAngleRefLines = React.useMemo(
    () =>
      userInputs
        .filter((u) => u.isValid && u.distance > 0)
        .map((u) => {
          const baseLabel = u.isApproaching ? t("npctTriggerApproach") : t("npctTriggerRecede");
          return {
            y: u.timestamp,
            label: `${baseLabel} — D ${formatUserInputD(u.distance)}`,
            color: u.isApproaching ? "violet.6" : "orange.6",
          };
        }),
    [userInputs, t],
  );

  const { expectedLeftAngleData, expectedRightAngleData } = React.useMemo(() => {
    const points = snapshots
      .filter((s, i) => i % DOWNSAMPLE === 0 && s.expectedConvergenceAngleDeg != null)
      .map((s) => ({ angleDeg: s.expectedConvergenceAngleDeg!, ts: s.timestamp }));
    return {
      expectedLeftAngleData: points.map((p) => ({ x: p.angleDeg, y: p.ts })),
      expectedRightAngleData: points.map((p) => ({ x: 80 - p.angleDeg, y: p.ts })),
    };
  }, [snapshots]);

  const { yAxisTicks, yAxisTickLabels } = React.useMemo(() => {
    const { ticks, labels } = computeDValueTicks(snapshots);
    return { yAxisTicks: ticks, yAxisTickLabels: labels };
  }, [snapshots]);

  return (
    <Paper p="md" radius="md" withBorder style={{ flexShrink: 0, width: 500 }}>
      <Title order={4} mb="md">
        {t("npctGazeAngleVsInvDist")}
      </Title>
      <ResponsiveContainer width="100%" height={700}>
        <ScatterChart margin={{ top: 20, right: 60, bottom: 50, left: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="number"
            name="angle"
            domain={[0, 80]}
            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
            tickFormatter={(v: number) => `${v <= 40 ? v : 80 - v}°`}
          >
            <Label value={t("npctGazeAngleDeg")} position="insideBottom" offset={-30} />
          </XAxis>
          <YAxis
            dataKey="y"
            type="number"
            name="invDist"
            reversed
            ticks={yAxisTicks}
            interval={0}
            tickFormatter={(v: number) => yAxisTickLabels.get(v) ?? ""}
          >
            <Label value={t("npctInverseDistance")} angle={-90} position="insideLeft" offset={10} />
          </YAxis>
          <ZAxis range={[0, 0]} />
          <Legend
            verticalAlign="top"
            content={() => {
              type LegendItem = {
                label: string;
                color: string;
                dashed?: boolean;
              };
              const items: LegendItem[] = [
                { label: t("npctLeftEyeGaze"), color: "#228be6" },
                { label: t("npctRightEyeGaze"), color: "#fa5252" },
                { label: t("npctExpectedAngle"), color: "#adb5bd", dashed: true },
              ];
              return (
                <ul
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 16,
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    fontSize: 12,
                  }}
                >
                  {items.map((item) => (
                    <li key={item.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width={22} height={10} aria-hidden>
                        <line
                          x1={0}
                          x2={22}
                          y1={5}
                          y2={5}
                          stroke={item.color}
                          strokeWidth={2}
                          strokeDasharray={item.dashed ? "5 4" : undefined}
                        />
                      </svg>
                      <span style={{ color: "#495057" }}>{item.label}</span>
                    </li>
                  ))}
                </ul>
              );
            }}
          />
          {gazeAngleRefLines.map((line) => {
            const stroke = line.color === "violet.6" ? "#7950f2" : "#fd7e14";
            return (
              <ReferenceLine
                key={`ref-${line.y}`}
                y={line.y}
                stroke={stroke}
                strokeDasharray="5 4"
                ifOverflow="extendDomain"
                label={{
                  value: line.label,
                  // Place approach labels on the right and recede labels on the
                  // left so close-together triggers don't overlap.
                  position: line.color === "violet.6" ? "insideTopRight" : "insideBottomRight",
                  fontSize: 11,
                  fill: stroke,
                }}
              />
            );
          })}
          <Scatter
            name={t("npctLeftEyeGaze")}
            data={leftEyeScatterData}
            fill="#228be6"
            line={{ stroke: "#228be6", strokeWidth: 1 }}
            shape={() => null}
            legendType="none"
          />
          <Scatter
            name={t("npctRightEyeGaze")}
            data={rightEyeScatterData}
            fill="#fa5252"
            line={{ stroke: "#fa5252", strokeWidth: 1 }}
            shape={() => null}
            legendType="none"
          />
          <Scatter
            name={t("npctExpectedAngle")}
            data={expectedLeftAngleData}
            fill="none"
            line={{ stroke: "#adb5bd", strokeWidth: 1, strokeDasharray: "5 4" }}
            shape={() => null}
            legendType="none"
          />
          <Scatter
            data={expectedRightAngleData}
            fill="none"
            line={{ stroke: "#adb5bd", strokeWidth: 1, strokeDasharray: "5 4" }}
            shape={() => null}
            legendType="none"
          />
        </ScatterChart>
      </ResponsiveContainer>
    </Paper>
  );
}
